import EngineCheckout from "../components/checkout/EngineCheckout";
import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
} from "react";
import { useLocalSearchParams, router } from "expo-router";
import { Dimensions, Platform, ScrollView } from "react-native";
import LBox from "@components/LBox";
import LText from "@components/LText";
import {
  useSafeAreaInsets,
  initialWindowMetrics,
} from "react-native-safe-area-context";
import BackgroundCircles from "../components/common/Background";
import Handle from "../components/common/Handle";
import { theme } from "@theme";
import Buttons from "../components";
import { type Access, useAccess } from "@hooks/trpc/useAccesses";
import CheckoutInfo from "../components/checkout/CheckoutInfo";
import OrderSummary, {
  DirectPaymentCopy,
} from "../components/checkout/OrderSummary";
import { useAccessViewport } from "../config";
import CheckoutButton from "../components/checkout/CheckoutButton";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";
import LInput from "@components/LInput";
import { z } from "zod";
import { useKeyboardOpen } from "@hooks/useKeyboardOpen";
import { isSafari } from "@utils/isSafari";
import { useAuth } from "@utils/firebaseAuth/auth";
import { prefetchAccessDetailHeroImage } from "@utils/accesses/access";
import { useAccessCheckoutQuote } from "@hooks/trpc/useAccessCheckoutQuote";

const CHECKOUT_QUOTE_INPUT_DEBOUNCE_MS = 250;

function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => clearTimeout(timeout);
  }, [delayMs, value]);

  return debouncedValue;
}

/** The checkout of an event that is not on the engine (old rail); an
 *  engine event's tickets, by deep link. */
function Checkout() {
  const { width: screenWidth, height: screenHeight } = useAccessViewport();
  const {
    accessId: accessIdParam,
    numberOfTickets: numberOfTicketsParam,
    pricePerTicket: pricePerTicketParam,
    unlockCode: unlockCodeParam,
  } = useLocalSearchParams<{
    accessId: string;
    numberOfTickets?: string;
    pricePerTicket?: string;
    unlockCode?: string;
  }>();
  const unlockCode =
    typeof unlockCodeParam === "string" ? unlockCodeParam : undefined;

  const { firebaseUser: user } = useAuth();
  const insets = useSafeAreaInsets();

  const [totalAmount, setTotalAmount] = useState(0);
  const [pricePerTicket, setPricePerTicket] = useState(0);
  const [useGems, setUseGems] = useState(true);
  const [email, setEmail] = useState("");
  const [directPaymentCopy, setDirectPaymentCopy] = useState<string | null>(
    null,
  );
  const isRebuildingRef = useRef(false);

  const keyboardOpen = useKeyboardOpen();
  const isIOSSafari = isSafari();
  const accessId = useMemo(() => {
    return Array.isArray(accessIdParam)
      ? accessIdParam[0]
      : accessIdParam || "1";
  }, [accessIdParam]);

  const parsedNumberOfTickets = numberOfTicketsParam
    ? Number(numberOfTicketsParam)
    : undefined;
  const parsedPricePerTicket = pricePerTicketParam
    ? Number(pricePerTicketParam)
    : undefined;
  const [numberOfTickets, setNumberOfTickets] = useState(
    () => parsedNumberOfTickets ?? 1,
  );

  const loaded: Access | undefined = useAccess(accessId).data;
  // Music unlocks, and merch is bought, in a sheet over its Details
  // (LIS-1038): a link here goes there.
  const inSheet = loaded?.type === "music" || loaded?.type === "merch";
  const access = loaded?.type === "merch" ? undefined : loaded;
  useEffect(() => {
    if (!inSheet) return;
    router.replace({
      pathname: "/(app)/access/[accessId]",
      params: { accessId },
    });
  }, [inSheet, accessId]);
  const checkoutQuoteInput = useMemo(() => {
    if (!access || access.type === "music" || access.engineEventId) {
      return null;
    }

    const bookingFee = access.bookingFee ?? 0;
    const customPrice =
      parsedPricePerTicket !== undefined
        ? Math.max(0, parsedPricePerTicket - bookingFee)
        : undefined;

    return {
      accessId,
      numberOfTickets,
      customPrice,
      useGems: true,
    };
  }, [access, accessId, numberOfTickets, parsedPricePerTicket]);
  const debouncedCheckoutQuoteInput = useDebouncedValue(
    checkoutQuoteInput,
    CHECKOUT_QUOTE_INPUT_DEBOUNCE_MS,
  );
  const { data: gemEconomicsQuote } = useAccessCheckoutQuote(
    debouncedCheckoutQuoteInput,
  );
  const confirmedAmountDueMinorUnits = Math.max(
    0,
    Math.round(totalAmount * 100),
  );

  const paymentResult = useNavigationStore((state) => state.paymentResult);
  const pathHistory = useNavigationStore((state) => state.pathHistory);

  const dismiss = useCallback(() => {
    router.back();
  }, []);

  const isValidEmail = useMemo(
    () => (user ? true : z.string().email().safeParse(email).success),
    [email, user],
  );

  // Rebuild navigation stack if we came directly to checkout (e.g., from Stripe redirect)
  // This ensures Details → Tickets → Checkout stack is always maintained
  useEffect(() => {
    if (
      Platform.OS !== "web" ||
      !access ||
      access.type === "music" ||
      paymentResult === "success" ||
      isRebuildingRef.current
    ) {
      return;
    }

    // Check if Details is in the navigation history
    // After rebuild, Details will be in history, so this prevents rebuild loops
    const hasDetailsInHistory = pathHistory.some(
      (path) =>
        path === `/access/${accessId}` ||
        (path.includes(`/access/${accessId}`) && !path.includes("/checkout")),
    );

    // Only rebuild if we came from Stripe redirect (paymentResult === "cancel")
    // or if Details isn't in the recent history
    const shouldRebuild = paymentResult === "cancel" || !hasDetailsInHistory;

    console.log(`[Checkout] shouldRebuild: ${shouldRebuild}`, {
      paymentResult,
      hasDetailsInHistory,
      pathHistory,
      accessId,
      parsedNumberOfTickets,
      parsedPricePerTicket,
      isRebuilding: isRebuildingRef.current,
    });

    if (shouldRebuild) {
      isRebuildingRef.current = true;
      console.log(
        "[Checkout] Rebuilding navigation stack: Details → Tickets → Checkout",
      );

      // First, replace current history entry with Details
      prefetchAccessDetailHeroImage(access);
      router.replace({
        pathname: "/(app)/access/[accessId]",
        params: { accessId },
      });

      // Then push Tickets and Checkout to rebuild the stack
      // Use requestAnimationFrame to ensure replace completes first
      requestAnimationFrame(() => {
        router.push({
          pathname: "/(app)/access/[accessId]/tickets",
          params: {
            accessId,
            ...(parsedNumberOfTickets && {
              numberOfTickets: parsedNumberOfTickets.toString(),
            }),
            ...(parsedPricePerTicket && {
              pricePerTicket: parsedPricePerTicket.toString(),
            }),
          },
        });

        requestAnimationFrame(() => {
          router.push({
            pathname: "/(app)/access/[accessId]/checkout",
            params: {
              accessId,
              ...(parsedNumberOfTickets && {
                numberOfTickets: parsedNumberOfTickets.toString(),
              }),
              ...(parsedPricePerTicket && {
                pricePerTicket: parsedPricePerTicket.toString(),
              }),
            },
          });
          // Reset the flag after a delay to allow pathHistory to update
          setTimeout(() => {
            isRebuildingRef.current = false;
          }, 100);
        });
      });
    }
  }, [
    access,
    accessId,
    pathHistory,
    paymentResult,
    parsedNumberOfTickets,
    parsedPricePerTicket,
  ]);

  useEffect(() => {
    if (paymentResult === "success") {
      dismiss();
    }
  }, [paymentResult, dismiss]);

  const bottomPadding = useMemo(() => {
    if (Platform.OS === "web") {
      if (keyboardOpen) {
        return Dimensions.get("window").height * (isIOSSafari ? 0.2 : 0.3);
      }
    }

    if (Platform.OS === "android") {
      return initialWindowMetrics?.insets?.bottom ?? 20;
    }

    return insets.bottom + theme.spacing.s3;
  }, [keyboardOpen, isIOSSafari, insets]);

  if (!access || access.type === "music") {
    return null;
  }

  if (access?.engineEventId)
    return <EngineCheckout accessId={accessId} onClose={dismiss} />;

  return (
    <LBox minHeight={screenHeight}>
      <BackgroundCircles imageUrl={null} />
      <ScrollView
        contentContainerStyle={{
          backgroundColor: "transparent",
          padding: 20,
          paddingTop: theme.spacing.s4,
          paddingBottom: bottomPadding,
          gap: theme.spacing.s3,
          flex: 1,
          width: screenWidth,
        }}
        showsVerticalScrollIndicator={false}
      >
        {Platform.OS !== "web" && <Handle />}
        <LBox flex={1} justifyContent="space-between">
          <LBox gap="m1">
            <LBox>
              <LBox
                position="relative"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <LBox width={32} />
                <LText variant="Heading/H2/SemiBold" textAlign="center">
                  Checkout
                </LText>
                <Buttons.common.Close
                  onPress={() => dismiss()}
                  sentryEventName="AccessTickets.Close"
                />
              </LBox>
            </LBox>

            <CheckoutInfo access={access} />
            <OrderSummary
              access={access}
              numberOfTickets={numberOfTickets}
              setNumberOfTickets={setNumberOfTickets}
              pricePerTicketParam={parsedPricePerTicket}
              setTotalAmount={setTotalAmount}
              setPricePerTicket={setPricePerTicket}
              setDirectPaymentCopy={setDirectPaymentCopy}
              gemEconomicsQuote={gemEconomicsQuote}
              useGemsSelected={useGems}
              onUseGemsChange={setUseGems}
            />
            {!user && (
              <LInput
                placeholder="Enter your email"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                placeholderTextColor={theme.colors.whiteTranslucent70}
                style={{
                  backgroundColor: theme.colors.whiteTranslucent10,
                  paddingLeft: theme.spacing.s4,
                  borderRadius: theme.borderRadii.m,
                }}
              />
            )}
          </LBox>

          <LBox gap="s3" style={{ marginBottom: theme.spacing.l3 }}>
            {directPaymentCopy ? (
              <DirectPaymentCopy copy={directPaymentCopy} />
            ) : null}
            <CheckoutButton
              access={access}
              numberOfTickets={numberOfTickets}
              pricePerTicket={unlockCode ? 0 : pricePerTicket}
              totalAmount={unlockCode ? 0 : confirmedAmountDueMinorUnits / 100}
              isValid={isValidEmail}
              email={user ? undefined : email}
              onPaymentSuccess={dismiss}
              unlockCode={unlockCode}
              useGems={useGems && gemEconomicsQuote?.applied === true}
              confirmedAmountDueMinorUnits={confirmedAmountDueMinorUnits}
            />
          </LBox>
        </LBox>
      </ScrollView>
    </LBox>
  );
}

export default Checkout;
